<template>
  <div v-on-click-outside="hideSwitch" class="relative">
    <div class="relative z-10">
      <slot v-bind="{ onSwitchClick, isVisible, currentLanguage }" />
    </div>
    <transition name="fade-transition">
      <div v-if="isVisible" :class="withBlackout && 'fixed inset-0'">
        <div
          v-if="withBlackout"
          class="absolute inset-0 bg-black bg-opacity-50"
          @click="hideSwitch"
        ></div>
        <div
          v-bind="$attrs"
          class="absolute z-50 max-w-[100vw] overflow-y-auto rounded-lg bg-white shadow"
        >
          <ul>
            <li
              v-for="lng in lngSwitchConfig.LANGUAGES"
              :key="lng.locale"
              class="tap-highlight-color-none flex cursor-pointer items-center justify-between px-4 pt-4 text-sm last:pb-4 hover:text-green-600 lg:text-lg"
              :class="{
                'text-green-600': isSelected(lng.locale),
              }"
              @click="selectLanguage(lng.locale)"
            >
              <span>
                {{ lng.text }}
              </span>
              <!-- prettier-ignore -->
              <svg v-if="isSelected(lng.locale)" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"><path stroke="#4EBC73" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m20 6.5-11 11-5-5"/></svg>
            </li>
          </ul>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { vOnClickOutside } from '@vueuse/components';

  import { injectAppContext } from '@shared/app-context';
  import { NavigationLib } from '@shared/lib';
  import type { AppLocale } from '@shared/i18n';
  import { lngSwitchConfig } from '../config';

  defineOptions({
    inheritAttrs: false, // Don't inherit, since all attrs are passed to the language switch modal
  });

  defineProps<{
    withBlackout?: boolean;
  }>();

  const emit = defineEmits<{
    /** Fires right before the page reloads on the picked locale. */
    onLanguageChange: [locale: AppLocale];
  }>();

  const appContext = injectAppContext();

  const isVisible = ref(false);

  const currentLanguage = computed(() => {
    if (appContext.locale in lngSwitchConfig.LANGUAGES) {
      return lngSwitchConfig.LANGUAGES[appContext.locale];
    }

    return lngSwitchConfig.DEFAULT_LANGUAGE;
  });

  const showSwitch = () => (isVisible.value = true);
  const hideSwitch = () => (isVisible.value = false);
  const isSelected = (locale: AppLocale) => locale === appContext.locale;

  const onSwitchClick = () => {
    if (isVisible.value) {
      hideSwitch();
    } else {
      showSwitch();
    }
  };

  const selectLanguage = (locale: AppLocale) => {
    if (appContext.locale !== locale) {
      emit('onLanguageChange', locale);
      NavigationLib.switchLocale(locale);
    }

    hideSwitch();
  };
</script>
